import React, { createContext, useState, useCallback, ReactNode } from "react";
import { authApi } from "../../api/api";
import { PaginationInfo } from "../../types/api";

interface Role {
  id?: number;
  name: string;
  guard_name?: string;
  permissions?: string[];
}

interface RolesContextType {
  roles: Role[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchRoles: (page?: number, perPage?: number) => Promise<void>;
  addRole: (role: Role) => Promise<void>;
  editRole: (id: number, role: Role) => Promise<void>;
  deleteRole: (id: number) => Promise<void>;
}

export const RolesContext = createContext<RolesContextType | undefined>(undefined);

interface RolesProviderProps {
  children: ReactNode;
}

export const RolesProvider: React.FC<RolesProviderProps> = ({ children }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchRoles = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await authApi.get(`/roles?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { roles: [...], pagination: {...} } }
      // API may return roles as array of strings or objects
      let rolesArray: string[] | any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.roles && Array.isArray(responseData.data.roles)) {
          rolesArray = responseData.data.roles;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          rolesArray = responseData.data;
          paginationData = responseData.pagination;
        } else if (responseData.roles && Array.isArray(responseData.roles)) {
          rolesArray = responseData.roles;
          paginationData = responseData.pagination;
        }
      } else if (responseData?.roles && Array.isArray(responseData.roles)) {
        rolesArray = responseData.roles;
      } else if (Array.isArray(responseData?.data)) {
        rolesArray = responseData.data;
      }
      
      const rolesData: Role[] = Array.isArray(rolesArray)
        ? rolesArray.map((roleItem: string | any, index: number) => {
            if (typeof roleItem === 'string') {
              return {
                id: index + 1,
                name: roleItem,
                guard_name: "web",
                permissions: [],
              };
            } else {
              return {
                id: roleItem.id || index + 1,
                name: roleItem.name || roleItem,
                guard_name: roleItem.guard_name || "web",
                permissions: Array.isArray(roleItem.permissions) ? roleItem.permissions : [],
              };
            }
          })
        : [];
      
      setRoles(rolesData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      setRoles([]); // Set empty array on error
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addRole = async (role: Role): Promise<void> => {
    try {
      const response = await authApi.post("/roles", {
        name: role.name,
        permissions: role.permissions || [],
      });
      const newRole = response.data?.data || response.data?.role || response.data;
      setRoles((prev) => [...prev, newRole]);
    } catch (error) {
      console.error("Failed to add role:", error);
      throw error;
    }
  };

  const editRole = async (id: number, role: Role): Promise<void> => {
    try {
      const response = await authApi.put(`/roles/${id}`, {
        name: role.name,
        permissions: role.permissions || [],
      });
      const updatedRole = response.data?.data || response.data?.role || response.data;
      setRoles((prev) =>
        prev.map((r) => (r.id === id ? updatedRole : r))
      );
    } catch (error) {
      console.error("Failed to edit role:", error);
      throw error;
    }
  };

  const deleteRole = async (id: number): Promise<void> => {
    try {
      await authApi.delete(`/roles/${id}`);
      setRoles((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("Failed to delete role:", error);
      throw error;
    }
  };

  // REMOVED auto-fetch useEffect - components will call fetchRoles explicitly with pagination

  return (
    <RolesContext.Provider
      value={{
        roles,
        pagination,
        loading,
        fetchRoles,
        addRole,
        editRole,
        deleteRole,
      }}
    >
      {children}
    </RolesContext.Provider>
  );
};

export default RolesProvider;
